import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import dynamic from 'next/dynamic';
import type { MapPageCluster, MapPageListing, SingleAddress } from '@zoocasa/go-search';
import { usePreferencesContext } from 'contexts/preferences';
import { useUserContext } from 'contexts/user';
import { useSavedListingContext } from 'contexts/saved-listing';
import {
  useModalContext,
  LOGIN_REGISTRATION_MODAL,
  TERMS_OF_USE_MODAL,
} from 'contexts/modal';
import {
  CALLOUT_EXPIRED_TYPE,
  CALLOUT_RENTED_TYPE,
  CALLOUT_RENT_TYPE,
  CALLOUT_SALE_TYPE,
  CALLOUT_SOLD_TYPE,
  type CalloutType,
  type FavouriteDelegate,
} from 'components/search-es/map/ClusterAnnotationCallout';
import {
  LISTING_TYPE,
  LOGIN_REQUIRED_TYPE,
  TERMS_OF_USE_REQUIRED_TYPE,
  type CompliancyDelegateType,
  type MapListingCardType,
} from 'components/map-listing-card';
import {
  AVAILABLE_STATUS,
  NOT_AVAILABLE_SOLD_STATUS,
} from 'contexts/preferences/listing-params/types';
import type { Bounds, LatLngPoint } from 'components/search-es/types';
import type { MapComponentMethods } from 'components/search-es/map/Map';
import type { ListingCardData } from 'components/listing-card';
import { PROPERTY_FAVOURITED_SOURCE_MAP_PIN } from '@bedrock/analytics';
import { getAcceptedTermsByUserFromCookies, isListingVisibleToUser, isVOWProvider } from 'utils/compliance';
import { getHideSquareFootage } from 'hooks/use-listings-compliance';
import { cleanDashedString } from 'data/addresses';
import styles from './map-pane.module.scss';

// `next/dynamic` cannot forward a `ref` to the map, so its imperative methods are
// picked up through the map's `onReady` callback instead (see `mapRef` below).
const MapComponent = dynamic(async () => (await import('../map/MapV2')).MapV2, { ssr: false });

export type MapPaneProps = {
  electedAddress?: SingleAddress;
  /** Filled with the map's imperative handle (used to pan when a saved search is selected). */
  mapRef?: MutableRefObject<MapComponentMethods | null>;
  clusters: MapPageCluster[];
  listings?: readonly ListingCardData[];
  highlightedListingId: number | null;
  isMapLoading: boolean;
  areaPolygonWkt?: string;
  center: LatLngPoint;
  zoom: number;
  isMapCenterPointLoading?: boolean;
  isMapCenterPointError?: boolean;
  onMapBoundingRegionChanged: (oldBounds: Bounds | null, newBounds: Bounds) => void;
  onMapZoomChanged: (oldZoom: number, newZoom: number) => void;
  /** Called whenever the mobile cluster/building listings drawer opens or closes. */
  onDrawerVisibilityChange?: (isOpen: boolean) => void;
  className?: string;
};

function listingUrl(listing: MapPageListing): string {
  return cleanDashedString(listing.addressUrlAbsolutePath || listing.listingUrlAbsolutePath || '');
}

export function MapPane({
  mapRef,
  clusters,
  listings = [],
  highlightedListingId,
  isMapLoading,
  areaPolygonWkt,
  center,
  zoom,
  isMapCenterPointLoading = false,
  isMapCenterPointError = false,
  onMapBoundingRegionChanged,
  onMapZoomChanged,
  onDrawerVisibilityChange,
  className,
}: MapPaneProps) {
  const { listingParams } = usePreferencesContext();
  const { isAuthenticated, user } = useUserContext();
  const { isSavedListing, addSavedListing, removeSavedListing, savedListings } = useSavedListingContext();
  const { openModal } = useModalContext();

  const calloutType: CalloutType = useMemo(() => {
    const filters = listingParams.filter;
    if (filters.rental) {
      if (filters.status === NOT_AVAILABLE_SOLD_STATUS) return CALLOUT_RENTED_TYPE;
      if (filters.status === AVAILABLE_STATUS) return CALLOUT_RENT_TYPE;
    } else if (filters.status === NOT_AVAILABLE_SOLD_STATUS) {
      return CALLOUT_SOLD_TYPE;
    } else if (filters.status === AVAILABLE_STATUS) {
      return CALLOUT_SALE_TYPE;
    }
    return CALLOUT_EXPIRED_TYPE;
  }, [listingParams.filter]);

  const findSlugById = useCallback((id: number): string | undefined => {
    const listingId = Number(id);
    const clusterSlug = clusters
      .flatMap(cluster => cluster.listings)
      .find(listing => Number(listing.id) === listingId)?.slug;
    if (clusterSlug) {
      return clusterSlug;
    }
    const listing = listings.find(item => Number(item.id) === listingId);
    if (listing == null) {
      return undefined;
    }
    const fromSlug = 'slug' in listing ? String((listing as { slug?: string }).slug ?? '') : '';
    if (fromSlug) {
      return fromSlug;
    }
    return listing.addressPathWithFallback.split('/').filter(Boolean).pop();
  }, [clusters, listings]);

  const optimisticFavouritesRef = useRef(new Map<number, boolean>());
  const [favouriteRevision, setFavouriteRevision] = useState(0);

  const bumpFavouriteRevision = useCallback(() => {
    setFavouriteRevision(revision => revision + 1);
  }, []);

  const readPersistedFavourite = useCallback((id: number): boolean => {
    const listingId = Number(id);
    if (savedListings.some(item => Number(item.listingId) === listingId)) {
      return true;
    }
    const slug = findSlugById(listingId);
    return isSavedListing(slug, listingId);
  }, [findSlugById, isSavedListing, savedListings]);

  const favouriteSyncToken = useMemo(
    () => savedListings
      .map(item => `${item.listingId ?? ''}:${item.addressSlug ?? ''}`)
      .sort()
      .join('|'),
    [savedListings]
  );

  // ListingCard favourites update `savedListings` directly and bypass this optimistic map.
  // Drop stale optimism whenever persisted favourites change so pin hearts stay in sync.
  useEffect(() => {
    optimisticFavouritesRef.current.clear();
  }, [favouriteSyncToken]);

  const isListingFavourited = useCallback((id: number) => {
    void favouriteRevision;
    const listingId = Number(id);
    if (optimisticFavouritesRef.current.has(listingId)) {
      return optimisticFavouritesRef.current.get(listingId) ?? false;
    }
    return readPersistedFavourite(listingId);
  }, [favouriteRevision, readPersistedFavourite]);

  const compliancyDelegate: CompliancyDelegateType = useMemo(() => ({
    getMapListingCardType(listing: MapPageListing): MapListingCardType {
      const isPrivateToUser = !isAuthenticated && isVOWProvider(listing.providerId);
      if (isPrivateToUser) {
        return LOGIN_REQUIRED_TYPE;
      }
      const { userBasedTerms, browserBasedTerms } = getAcceptedTermsByUserFromCookies(user?.id);
      const isVisible = isListingVisibleToUser(
        isPrivateToUser,
        listing.providerId,
        browserBasedTerms,
        userBasedTerms
      );
      return isVisible ? LISTING_TYPE : TERMS_OF_USE_REQUIRED_TYPE;
    },
    mustShowProviderLogo: () => false,
    shouldOpenAsModal: () => false,
    hideSquareFootage(listing: MapPageListing) {
      return getHideSquareFootage(listing.providerId, listing.standardStatus);
    },
  }), [isAuthenticated, user?.id]);

  const favouriteDelegate: FavouriteDelegate = useMemo(() => ({
    isFavourite: isListingFavourited,
    toggleFavourite(id: number) {
      const listingId = Number(id);
      const next = !isListingFavourited(listingId);
      optimisticFavouritesRef.current.set(listingId, next);
      bumpFavouriteRevision();

      if (isAuthenticated) {
        if (!next) {
          removeSavedListing(listingId, { source: PROPERTY_FAVOURITED_SOURCE_MAP_PIN });
          return false;
        }
        addSavedListing({ listingId, isModal: true, source: PROPERTY_FAVOURITED_SOURCE_MAP_PIN });
        return true;
      }
      openModal(LOGIN_REGISTRATION_MODAL, {
        callback: () => {
          optimisticFavouritesRef.current.set(listingId, true);
          bumpFavouriteRevision();
          addSavedListing({ listingId, isModal: true, source: PROPERTY_FAVOURITED_SOURCE_MAP_PIN });
        },
      });
      optimisticFavouritesRef.current.delete(listingId);
      bumpFavouriteRevision();
      return false;
    },
  }), [addSavedListing, bumpFavouriteRevision, isAuthenticated, isListingFavourited, openModal, removeSavedListing]);

  const openListing = useCallback((listing: MapPageListing) => {
    const path = listingUrl(listing);
    if (path) {
      window.open(path, '_blank');
    }
  }, []);

  const handleListingClick = useCallback((listing: MapPageListing) => {
    const cardType = compliancyDelegate.getMapListingCardType(listing);
    if (cardType === LOGIN_REQUIRED_TYPE) {
      openModal(LOGIN_REGISTRATION_MODAL, { callback: () => openListing(listing) });
      return;
    }
    if (cardType === TERMS_OF_USE_REQUIRED_TYPE) {
      openModal(TERMS_OF_USE_MODAL, {
        providerId: listing.providerId,
        callback: () => openListing(listing),
      });
      return;
    }
    openListing(listing);
  }, [compliancyDelegate, openListing, openModal]);

  const handleMapReady = useCallback((methods: MapComponentMethods) => {
    if (mapRef) mapRef.current = methods;
  }, [mapRef]);

  return (
    <div data-testid="AreaPageV2-MapPane" className={`${styles.root}${className ? ` ${className}` : ''}`}>
      <MapComponent
        onReady={handleMapReady}
        clusters={clusters}
        listings={listings}
        highlightedListingId={highlightedListingId}
        showsLoading={isMapLoading}
        showsSidePanel={false}
        isSidePanelFullScreen={false}
        center={center}
        zoom={zoom}
        isMapCenterPointLoading={isMapCenterPointLoading}
        isMapCenterPointError={isMapCenterPointError}
        calloutType={calloutType}
        compliancyDelegate={compliancyDelegate}
        favouriteDelegate={favouriteDelegate}
        favouriteSyncToken={favouriteSyncToken}
        onSavedSearchFilterClick={() => undefined}
        onSingleListingClusterClick={() => undefined}
        onListingClick={handleListingClick}
        onTermsRequiredClick={handleListingClick}
        onMapBoundingRegionChanged={onMapBoundingRegionChanged}
        onMapZoomChanged={onMapZoomChanged}
        onDrawerVisibilityChange={onDrawerVisibilityChange}
        polygonWkt={areaPolygonWkt}
        isHybridPage
        isAiSearch={false}
      />
    </div>
  );
}
